import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientItemEntity } from './client-item.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { InvoiceDetailEntity } from './invoice-detail.entity';
import { InvoiceFileEntity } from './invoice-file.entity';
import { MSmPaymentTransactionInvoiceEntity } from './m-sm-payment-transaction-invoice.entity';
import { TaxEntity } from './tax.entity';
import { TermsCodeEntity } from './terms-code.entity';

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'], {})
@Index('fk_tax__id', ['fkTaxId'], {})
@Index('invoice__deleted_userId_index', ['deletedUserId'], {})
@Index(
	'invoice__fk_client__id_date_type__deleted_userId_index',
	['fkClientId', 'date', 'type', 'deletedUserId'],
	{},
)
@Index('invoice__fk_client__id_fk', ['fkClientId'], {})
@Index('invoice__fk_warehouse__id', ['fkWarehouseId'], {})
@Index('idx_invoice_billing_account', ['fkClientIdBilling'], {})
@Index(
	'invoice__fk_client_address__id_billing_fk',
	['fkClientAddressIdBilling'],
	{},
)
@Index('invoice_date__deleted_userId_index', ['date', 'deletedUserId'], {})
@Index('invoice_date_index', ['date'], {})
@Index('invoice_fk_user__id_date', ['fkUserId', 'date'], {})
@Entity('invoice')
export class InvoiceEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('date', { name: 'date', nullable: true })
	public date!: string | null;

	@Column('int', { name: 'numberOfLines', nullable: true })
	public numberOfLines!: number | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	/**
	 * PD-15059 — the payment processing fee stamp, copied from the source
	 * order (same semantics: the CONFIGURED value — "3" with unit '%' or a
	 * flat amount with '$'). The invoice total INCLUDES the computed fee so
	 * it ties exactly to what the customer's payment method is charged; the
	 * fee's dollar value is derivable as
	 * `total − (subtotal + freight + tax − discount)`, which is also the
	 * pre-fee base a gateway charge should be requested for (the payments
	 * layer re-applies the fee at charge time).
	 */
	@Column('varchar', {
		name: 'm_sm_payment_fee_unit',
		nullable: true,
		length: 32,
	})
	public mSmPaymentFeeUnit!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_fee_amount',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeAmount!: string;

	/**
	 * PD-15143 — the processing fee on this invoice, in DOLLARS, stored rather
	 * than derived. `total` includes it.
	 *
	 * The value changes BASIS as the invoice progresses, though it is always
	 * part of the total:
	 *
	 *   - before any payment it is PROJECTED from the stamped rate and the
	 *     document's payment method, so the invoice states what the customer
	 *     will be charged;
	 *   - once payments post it is the SUM of those transactions' own fees,
	 *     which is what makes split tender exact — a $100 invoice settled half
	 *     from account funds and half on a 3% card carries $1.50, not $3.00, and
	 *     no projection from a single rate could have known that;
	 *   - once the invoice is paid in full it is SETTLED and stops moving.
	 *
	 * An open invoice's total may therefore change — one issued expecting ACH
	 * and later paid by card genuinely costs more. A difference arising against
	 * a closed invoice belongs in an explicit adjustment, not a silent edit.
	 *
	 * Replaces reading the fee as `total − (subtotal + freight + tax − discount)`.
	 * That residual was indistinguishable from any OTHER gap between a total and
	 * its lines, which is how 15,263 invoices on one tenant printed an unapplied
	 * discount as a "Processing Fee" (PD-15115).
	 */
	@Column('decimal', {
		name: 'm_sm_payment_fee_total',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeTotal!: string;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	/**
	 * PD-15754 — the summed subtotal and GP of this invoice's lines flagged
	 * `excludeFromSales` (freight-like charges such as restocking fees). They
	 * are INCLUDED in `subtotal` and `totalGp`, which stay the invoice's real
	 * totals; sales reports subtract them so they need no join to the lines.
	 * NULL when no line is flagged. Written by the integrations (pd-model
	 * rollup) and by SM2's own invoice total recalculation.
	 */
	@Column('decimal', {
		name: 'nonSalesSubtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public nonSalesSubtotal!: string | null;

	@Column('decimal', {
		name: 'nonSalesGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public nonSalesGp!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'type', nullable: true, length: 10 })
	public type!: string | null;

	/**
	 * PD-15026 — why a credit was issued, from the fixed `CREDIT_MEMO_REASONS`
	 * vocabulary. Populated only on credit memos (`type = InvoiceType.CREDIT_MEMO`).
	 *
	 * A real column rather than a note or a custom field because credits have
	 * to be reportable in aggregate — "how much are we crediting, for what, and
	 * to whom" is the control on a process that hands money back. A fixed
	 * vocabulary in its own column groups; free text in a JSON blob does not.
	 */
	@Column('varchar', { name: 'creditMemoReason', nullable: true, length: 64 })
	public creditMemoReason!: string | null;

	/** PD-15026 — free-text detail on the credit, e.g. which delivery it came from. */
	@Column('varchar', { name: 'creditMemoNote', nullable: true, length: 255 })
	public creditMemoNote!: string | null;

	@Column('varchar', { name: 'status', nullable: true, length: 32 })
	public status!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 255 })
	public externalId2!: string | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	/**
	 * PD-15849 — the billing account this invoice belongs to: the ordering
	 * client, or its parent when the client is billed with parent. Stamped
	 * at creation from the client's resolved billing account and never
	 * re-derived afterwards, because flipping "Bill with parent" is
	 * non-retroactive — only an explicit move restates existing rows.
	 *
	 * Declared NULLABLE deliberately. Our database sync is additive only
	 * (it never ALTERs an existing column), so a NOT NULL column could not
	 * be added to a table that already has rows. The PD-15849 migration
	 * backfills `fk_client__id_billing = _fk_client__id` for every existing
	 * row, but the column stays nullable so a tenant whose migration has
	 * not run yet still reads correctly: NULL means "billed to
	 * `_fk_client__id`". Read it through `billingAccountOf`
	 * (`libs/shared/src/utils/billing-account.ts`), never directly.
	 */
	@Column('int', {
		name: 'fk_client__id_billing',
		nullable: true,
		unsigned: true,
		comment:
			'Billing account for this invoice (the parent when billed with parent). NULL = billed to _fk_client__id.',
	})
	public fkClientIdBilling!: number | null;

	/**
	 * PD-15754 (PD-6295) — the sales rep credited with this invoice. Stamped
	 * once when the invoice is created or imported (the ERP's invoice rep, else
	 * the linked order's rep, else the client's rep at that moment) and never
	 * moved when the client is reassigned, so past sales stay with the rep who
	 * made them. Sales reports attribute and scope on this column.
	 */
	@Column('int', {
		name: 'fk_user__id',
		nullable: true,
		unsigned: true,
		comment:
			'Sales rep credited with this invoice, stamped at creation or import. Not moved when the client is reassigned.',
	})
	public fkUserId!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdBilling!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	/**
	 * PD-17225 — the warehouse credited with the sale, which sales by location
	 * counts. Filled by the ERP import (or from the order for invoices made in
	 * Workd). Column and index only, no database foreign key: database sync
	 * would otherwise rebuild this large table to add the constraint. Nullable,
	 * so the legacy app and imports that do not fill it are unaffected.
	 */
	@Column('int', { name: 'fk_warehouse__id', nullable: true, unsigned: true })
	public fkWarehouseId!: number | null;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_outstandingBalance',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public mSmPaymentOutstandingBalance!: string | null;

	@Column('decimal', {
		name: 'shipCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public shipCost!: string | null;

	@Column('varchar', { name: 'paymentTerm', nullable: true, length: 255 })
	public paymentTerm!: string | null;

	/**
	 * PD-15847 — the payment method type carried over from the order this
	 * invoice was raised from, so statements and lists can say how the
	 * invoice is meant to be settled (Company Credit / Cash / Check as well
	 * as Credit Card / ACH). NULL on pre-existing invoices.
	 */
	@Column('varchar', {
		name: 'paymentMethodType',
		nullable: true,
		length: 64,
		comment:
			'Payment method type copied from the source order (PaymentMethodTypeEnum value).',
	})
	public paymentMethodType!: string | null;

	@Column('int', {
		name: 'fk_terms_code__id',
		nullable: true,
		unsigned: true,
	})
	public fkTermsCodeId!: number | null;

	@ManyToOne(() => TermsCodeEntity, (termsCode) => termsCode.invoices, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_terms_code__id', referencedColumnName: 'id' }])
	public fkTermsCode!: TermsCodeEntity | null;

	@Column('decimal', {
		name: 'earlyPayDiscountPercentSnapshot',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public earlyPayDiscountPercentSnapshot!: string | null;

	@Column('int', {
		name: 'earlyPayDiscountWindowDaysSnapshot',
		nullable: true,
	})
	public earlyPayDiscountWindowDaysSnapshot!: number | null;

	@Column('date', { name: 'dueDate', nullable: true })
	public dueDate!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('int', { name: 'fk_tax__id', nullable: true, unsigned: true })
	public fkTaxId!: number | null;

	@Column('decimal', {
		name: 'taxRate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public taxRate!: string | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(() => ClientEntity, (client) => client.fkInvoiceIdLast2, {})
	public clients!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkInvoice, {})
	public clients2!: Array<ClientEntity>;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkInvoiceIdLast2,
		{},
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany(
		() => ClientItemEntity,
		(clientItem) => clientItem.fkInvoiceIdLast2,
		{},
	)
	public clientItems!: Array<ClientItemEntity>;

	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkInvoice, {})
	public clientItems2!: Array<ClientItemEntity>;

	@ManyToOne(() => CompanyEntity, (company) => company.invoices, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => TaxEntity, (tax) => tax.invoices, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_tax__id', referencedColumnName: 'id' }])
	public fkTax!: TaxEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.invoices,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(() => ClientEntity, (client) => client.invoices, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	// PD-15849 — the billing account (see `fkClientIdBilling`).
	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_billing', referencedColumnName: 'id' }])
	public fkClientBilling!: ClientEntity | null;

	@OneToMany(
		() => InvoiceFileEntity,
		(invoiceFile) => invoiceFile.fkInvoice,
		{},
	)
	public invoiceFiles!: Array<InvoiceFileEntity>;

	@OneToMany(
		() => InvoiceDetailEntity,
		(invoiceDetail) => invoiceDetail.invoice,
		{},
	)
	public invoiceDetails!: Array<InvoiceDetailEntity>;

	@OneToMany(
		() => MSmPaymentTransactionInvoiceEntity,
		(mSmPaymentTransactionInvoice) =>
			mSmPaymentTransactionInvoice.mSmPaymentFkInvoice,
		{},
	)
	public mSmPaymentTransactionInvoices!: Array<MSmPaymentTransactionInvoiceEntity>;
}
